import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../../api/config";
import "./GhostAssistent.css";

const API = `${API_BASE}/ki`;

// ── SVG Ghost ──────────────────────────────────────────────────────────────
function GhostSvg() {
  return (
    <svg viewBox="0 0 64 72" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Körper */}
      <path
        d="M8 32C8 18 18 8 32 8C46 8 56 18 56 32V60L49 54L42 60L35 54L28 60L21 54L14 60L8 54V32Z"
        fill="url(#ghost-grad)"
        opacity="0.95"
      />
      {/* Augen – links */}
      <ellipse cx="24" cy="30" rx="5" ry="6" fill="#1e1b4b" />
      <ellipse cx="25.5" cy="28.5" rx="2" ry="2.5" fill="white" />
      {/* Augen – rechts */}
      <ellipse cx="40" cy="30" rx="5" ry="6" fill="#1e1b4b" />
      <ellipse cx="41.5" cy="28.5" rx="2" ry="2.5" fill="white" />
      {/* Mund */}
      <path d="M26 40 Q32 46 38 40" stroke="#1e1b4b" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      {/* Glanz */}
      <ellipse cx="36" cy="15" rx="5" ry="3" fill="white" opacity="0.15" transform="rotate(-20 36 15)" />
      <defs>
        <linearGradient id="ghost-grad" x1="8" y1="8" x2="56" y2="60" gradientUnits="userSpaceOnUse">
          <stop stopColor="#c4b5fd" />
          <stop offset="1" stopColor="#a78bfa" />
        </linearGradient>
      </defs>
    </svg>
  );
}

// ── Quick-Action Chips ─────────────────────────────────────────────────────
const CHIPS = [
  { label: "📁 Rename Me erklären", text: "Wie funktioniert das Rename-Me System?" },
  { label: "🔧 Wo finde ich...?",   text: "Was kann ich alles in diesem Programm machen?" },
  { label: "📊 STRG Eintrag",       text: "Wie füge ich einen neuen STRG-Eintrag hinzu?" },
  { label: "🏠 Prefix & Objekte",   text: "Welche Prefixe gibt es und was bedeuten sie?" },
  { label: "🚗 Fahrtenbuch",        text: "Wie benutze ich das Fahrtenbuch?" },

];

// ── Markdown-Light Renderer ────────────────────────────────────────────────
function renderText(text) {
  // **bold**, `code`, zeilenumbrüche
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\n)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**"))
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (p.startsWith("`") && p.endsWith("`"))
      return <code key={i}>{p.slice(1, -1)}</code>;
    if (p === "\n")
      return <br key={i} />;
    return p;
  });
}

// ── Hauptkomponente ────────────────────────────────────────────────────────
const ADMIN_HASH = "QWRtaW4xMSE="; // btoa("Admin11!")

export default function GhostAssistent() {
  const navigate = useNavigate();
  const [offen, setOffen] = useState(false);
  const [schliesst, setSchliesst] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [hasUnread, setHasUnread] = useState(true);
  // Admin Login
  const [adminMode, setAdminMode] = useState(false);  // Passwort-Eingabe aktiv?
  const [adminPw, setAdminPw] = useState("");
  const [adminFehler, setAdminFehler] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const adminRef = useRef(null);

  // Auto-Scroll
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input beim Öffnen
  useEffect(() => {
    if (offen) {
      setTimeout(() => inputRef.current?.focus(), 300);
      setHasUnread(false);
    }
  }, [offen]);

  // Focus Admin-Input wenn adminMode aktiv
  useEffect(() => {
    if (adminMode) setTimeout(() => adminRef.current?.focus(), 50);
  }, [adminMode]);

  const oeffnen = () => {
    setSchliesst(false);
    setAdminMode(false);
    setAdminPw("");
    setAdminFehler(false);
    setOffen(true);
    setHasUnread(false);
  };

  const schliessen = () => {
    setSchliesst(true);
    setTimeout(() => {
      setOffen(false);
      setSchliesst(false);
      setAdminMode(false);
      setAdminPw("");
      setAdminFehler(false);
    }, 200);
  };

  const toggleChat = () => {
    if (offen) schliessen();
    else oeffnen();
  };

  const oeffneAdmin = () => {
    setAdminMode(true);
    setAdminPw("");
    setAdminFehler(false);
  };

  const pruefeAdminPw = () => {
    if (btoa(adminPw) === ADMIN_HASH) {
      schliessen();
      navigate("/ki-admin");
    } else {
      setAdminFehler(true);
      setAdminPw("");
      setTimeout(() => setAdminFehler(false), 2000);
    }
  };

  const oeffneKiLernen = () => {
    schliessen();
    navigate("/ki-lernen");
  };

  // Session starten (lazy – erst beim ersten Senden)
  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;
    const resp = await fetch(`${API}/chat/start`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: "frontend" }),
    });
    if (!resp.ok) throw new Error("Session start failed");
    const data = await resp.json();
    setSessionId(data.session_id);
    return data.session_id;
  }, [sessionId]);

  // Nachricht senden
  const senden = useCallback(async (text) => {
    const nachricht = (text || input).trim();
    if (!nachricht || sending) return;
    setInput("");
    setSending(true);

    setMessages((prev) => [...prev, { role: "user", content: nachricht }]);
    setMessages((prev) => [...prev, { role: "typing" }]);

    try {
      const sid = await ensureSession();
      const resp = await fetch(`${API}/chat/${sid}/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: nachricht }),
      });
      if (!resp.ok) throw new Error("Antwort fehlgeschlagen");
      const data = await resp.json();

      setMessages((prev) => [
        ...prev.filter((m) => m.role !== "typing"),
        { role: "assistant", content: data.message },
      ]);

      // Dot anzeigen wenn Chat geschlossen
      if (!offen) setHasUnread(true);
    } catch (err) {
      setMessages((prev) => [
        ...prev.filter((m) => m.role !== "typing"),
        { role: "error", content: "Verbindungsfehler – ist der Server an? (localhost:8000)" },
      ]);
    } finally {
      setSending(false);
    }
  }, [input, sending, ensureSession, offen]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      senden();
    }
  };

  // Textarea auto-grow
  const handleInput = (e) => {
    const ta = e.target;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 100) + "px";
    setInput(ta.value);
  };

  return (
    <div className="ghost-assistent-wrapper">
      {/* Chat Fenster */}
      {offen && (
        <div className={`ghost-chat-fenster ${schliesst ? "schliessen" : ""}`}>
          {/* Header */}
          <div className="ghost-header">
            <span className="ghost-header-icon">👻</span>
            <div className="ghost-header-info">
              <div className="ghost-header-name">imho Assistent</div>
              <div className="ghost-header-status">
                <span className="ghost-status-dot" />
                Online – frag mich alles
              </div>
            </div>
            <button className="ghost-schliessen-btn" onClick={schliessen} title="Schließen">
              ✕
            </button>
          </div>

          {/* Quick-Action Chips */}
          <div className="ghost-vorschlaege">
            {CHIPS.map((chip) => (
              <button
                key={chip.label}
                className="ghost-chip"
                onClick={() => senden(chip.text)}
                disabled={sending}
              >
                {chip.label}
              </button>
            ))}
            {/* KI Lernen – navigiert direkt */}
            <button className="ghost-chip chip-lernen" onClick={oeffneKiLernen}>
              🎓 KI Lernen
            </button>
            {/* Admin – öffnet Passwort-Eingabe */}
            <button className="ghost-chip chip-admin" onClick={oeffneAdmin}>
              🔐 Admin
            </button>
          </div>

          {/* Nachrichten */}
          <div className="ghost-nachrichten">
            {messages.length === 0 && (
              <div className="ghost-welcome">
                <span className="ghost-welcome-emoji">👻</span>
                <h3>Hey! Ich bin dein imho-Assistent</h3>
                <p>
                  Stell mir alles rund ums Programm –<br />
                  Rename‑Me, Datenbank, Fahrtenbuch,<br />
                  oder einfach wie irgendwas funktioniert.
                </p>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`ghost-msg ${msg.role}`}>
                {msg.role !== "user" && (
                  <span className="ghost-msg-avatar">
                    {msg.role === "typing" ? "👻" : msg.role === "error" ? "⚠️" : "👻"}
                  </span>
                )}
                <div className="ghost-msg-bubble">
                  {msg.role === "typing" ? (
                    <div className="ghost-typing-dots">
                      <span /><span /><span />
                    </div>
                  ) : (
                    renderText(msg.content)
                  )}
                </div>
                {msg.role === "user" && (
                  <span className="ghost-msg-avatar">😊</span>
                )}
              </div>
            ))}
            <div ref={endRef} />
          </div>

          {/* Input */}
          {adminMode ? (
            /* Admin Passwort-Eingabe */
            <div className={`ghost-admin-login${adminFehler ? " fehler" : ""}`}>
              <div className="ghost-admin-label">
                🔐 Admin-Passwort eingeben
                <button className="ghost-admin-abbruch" onClick={() => setAdminMode(false)}>✕</button>
              </div>
              <div className="ghost-input-bereich">
                <input
                  ref={adminRef}
                  className="ghost-input"
                  type="password"
                  placeholder={adminFehler ? "❌ Falsches Passwort" : "Passwort…"}
                  value={adminPw}
                  onChange={e => { setAdminPw(e.target.value); setAdminFehler(false); }}
                  onKeyDown={e => e.key === "Enter" && pruefeAdminPw()}
                />
                <button
                  className="ghost-sende-btn"
                  onClick={pruefeAdminPw}
                  disabled={!adminPw}
                >
                  ➤
                </button>
              </div>
            </div>
          ) : (
            /* Normaler Chat-Input */
            <div className="ghost-input-bereich">
              <textarea
                ref={inputRef}
                className="ghost-input"
                value={input}
                onChange={handleInput}
                onKeyDown={handleKeyDown}
                placeholder="Schreib etwas… (Enter = senden)"
                rows={1}
                disabled={sending}
              />
              <button
                className="ghost-sende-btn"
                onClick={() => senden()}
                disabled={sending || !input.trim()}
                title="Senden"
              >
                ➤
              </button>
            </div>
          )}
        </div>
      )}

      {/* Ghost Button */}
      <button
        className={`ghost-btn ${offen ? "offen" : ""}`}
        onClick={toggleChat}
        title="imho Assistent"
        aria-label="imho KI Assistent öffnen"
      >
        <GhostSvg />
        {hasUnread && !offen && <span className="ghost-dot" />}
        <span className="ghost-tooltip">imho Assistent</span>
      </button>
    </div>
  );
}
